import {NextRouter, useRouter} from 'next/router';
import {isValidElement, useCallback, useContext, useEffect} from 'react';
import {usePathname} from 'next/navigation';
import Router from 'next/dist/shared/lib/router/router';
import {TGetListingRequest} from 'plugins/modules/listing/api/api';
import {TGetListingTransformedResponse} from 'plugins/modules/listing/types';
import {SORT_PARAM} from 'plugins/modules/sorting/constants';
import {useCatalogSortGtag} from 'hooks/analytics/gtag/catalog/useCatalogSortGtag';
import {SORT_LIST} from 'modules/products/constants';
import {SelectTooltipOption} from 'modules/products/components/SelectTooltipOption';
import type {ReadonlyURLSearchParams} from 'next/navigation';
import {PAGINATION_PARAM} from 'core/constants';
import {FilterPickupTodayButtonContext} from 'plugins/modules/filter/context/FilterPickupTodayButtonContext';
import {CookieService} from 'core/services/CookieService';
import {isBrowserContext} from 'core/helpers';

interface IUseInitListing<Request, Response> {
    router: NextRouter;
    searchParams: URLSearchParams | ReadonlyURLSearchParams;
    fetchData: (
        setArgs: (value: Request) => Request,
        preferCacheValue?: boolean,
    ) => Promise<{data: Response | undefined; status: string}>;
}

export const useListingHandlers = <
    Product,
    Request extends TGetListingRequest,
    Response extends TGetListingTransformedResponse<Product>,
>({
    router,
    searchParams,
    fetchData,
}: IUseInitListing<Request, Response>) => {
    const pathname = usePathname();
    const {replace} = useRouter();
    const {setHandleResetPagination} = useContext(FilterPickupTodayButtonContext);

    const catalogSortGtag = useCatalogSortGtag();

    const toMutableParams = useCallback((params: URLSearchParams | ReadonlyURLSearchParams) => {
        return new URLSearchParams(Array.from(params.entries()));
    }, []);

    const onPageChange = useCallback(
        async (nextPageNumber: number, method: Router['replace'] | Router['push'] | undefined = replace) => {
            const mutableParams = toMutableParams(searchParams);

            if (1 === nextPageNumber) {
                mutableParams.delete(PAGINATION_PARAM);
            } else {
                mutableParams.set(PAGINATION_PARAM, nextPageNumber.toString());
            }

            const search = mutableParams.toString();
            const query = search ? `?${search}` : '';

            method(`${pathname}${query}`, undefined, {shallow: true});
        },
        [pathname, replace, searchParams, toMutableParams],
    );

    const onSortChange = useCallback(
        async (sort?: string, method: Router['replace'] | Router['push'] | undefined = replace) => {
            const mutableParams = toMutableParams(searchParams);
            if (sort) {
                mutableParams.set(SORT_PARAM, sort);
                mutableParams.delete(PAGINATION_PARAM);

                const cookieService = CookieService.getInstance();
                cookieService.setInsecureCookie('USER_SORT', sort);
            }

            const search = mutableParams.toString();
            const query = search ? `?${search}` : '';

            method(`${pathname}${query}`, undefined, {shallow: true});
        },
        [pathname, replace, searchParams, toMutableParams],
    );

    const handleSort = useCallback(
        async ({value}: {value: string}) => {
            await onSortChange(value);
            await fetchData((args: Request) => {
                const sort = value;

                return {
                    ...args,
                    sort,
                    pageNumber: 1,
                    pagination: {
                        isManual: true,
                        firstPage: 1,
                        lastPage: 1,
                    },
                };
            }).then(() => {
                const sortItem = SORT_LIST.find((item) => item.param === value);

                if (!sortItem) {
                    return;
                }

                if (isValidElement(sortItem.title) && sortItem.title.type === SelectTooltipOption) {
                    catalogSortGtag((sortItem.title.props as {content: string}).content);
                } else if ('string' === typeof sortItem.title) {
                    catalogSortGtag(sortItem.title);
                }
            });
        },
        [catalogSortGtag, fetchData, onSortChange],
    );

    const handleSetPage = useCallback(
        async (nextPageNumber: number) => {
            const {data, status} = await fetchData((args) => {
                return {
                    ...args,
                    pageNumber: nextPageNumber,
                    pagination: {
                        isManual: true,
                        lastPage: nextPageNumber,
                        firstPage: nextPageNumber,
                    },
                };
            }, false);
            if (data?.pagination?.pageNumber && 'rejected' !== status) {
                await onPageChange(data.pagination?.pageNumber, router.push);
            }
        },
        [fetchData, onPageChange, router.push],
    );

    useEffect(() => {
        setHandleResetPagination?.(() => () => {
            // список схлопывается до 1 страницы, и без сброса скролла триггер
            // бесконечной подгрузки (IntersectionObserver) сразу же перезапросит следующую страницу
            if (isBrowserContext()) {
                window.scrollTo({top: 0, left: window.scrollX});
            }
            handleSetPage(1);
        });

        return () => {
            setHandleResetPagination?.(() => {});
        };
    }, [handleSetPage, onPageChange, setHandleResetPagination]);

    const handleLoadNext = useCallback(async () => {
        const {data, status} = await fetchData((args) => {
            const lastPage = args.pagination.lastPage + 1;

            return {
                ...args,
                pageNumber: lastPage,
                pagination: {
                    ...args.pagination,
                    isManual: false,
                    lastPage,
                },
            };
        });

        if (data?.pagination?.pageNumber && 'rejected' !== status) {
            await onPageChange(data.pagination?.pageNumber, router.push);
        }
    }, [fetchData, onPageChange, router.push]);

    const handleLoadPrev = useCallback(async () => {
        const {data, status} = await fetchData((args) => {
            const firstPage = args.pagination.firstPage - 1;

            return {
                ...args,
                pageNumber: firstPage,
                pagination: {
                    ...args.pagination,
                    isManual: false,
                    firstPage,
                },
            };
        });

        if (data?.pagination?.pageNumber && 'rejected' !== status) {
            await onPageChange(data.pagination?.pageNumber, router.push);
        }
    }, [fetchData, onPageChange, router.push]);

    return {
        handleSetPage,
        handleLoadPrev,
        handleLoadNext,
        handleSort,
    };
};
